import { zodResolver } from '@hookform/resolvers/zod'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import ThermostatIcon from '@mui/icons-material/Thermostat'
import {
  Alert,
  Box,
  CardHeader,
  Divider,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import { useQueryClient } from '@tanstack/react-query'
import type { TFunction } from 'i18next'
import { DateTime } from 'luxon'
import type { ReactNode } from 'react'
import { useEffect, useMemo, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import CustomTypography from '@/components/dataDisplay/CustomTypography'
import CustomButton from '@/components/inputs/CustomButton'
import SelectFieldController from '@/components/inputs/SelectFieldController'
import { useAlertContext } from '@/contexts/AlertContext'
import { ActivationGroupRouteInformation } from '@/features/activationGroup/constants'
import { isActivatableSupported } from '@/features/activationGroup/utils/activationGroups'
import PortSettingValue, { OperatorGlyph } from '@/features/resource/components/PortSettingValue'
import ResourceCard from '@/features/resource/components/ResourceCard'
import ResourcePortsSection from '@/features/resource/components/ResourcePortsSection'
import { useResourceData } from '@/features/resource/contexts/ResourceDataContext'
import { GET_RESOURCE_API_ID } from '@/features/resource/endpoints/resources'
import { usePortHighlight } from '@/features/resource/hooks/usePortHighlight'
import { useResourceActivationGroupsQuery } from '@/features/resource/hooks/useResourceActivationGroupsQuery'
import { useUpdateControlPortMeasureMutation } from '@/features/resource/hooks/useUpdateControlPortMeasureMutation'
import { useUpdateMeteringPortRelationMutation } from '@/features/resource/hooks/useUpdateMeteringPortRelationMutation'
import { getMeasureSchema, getMeteringRelationSchema } from '@/features/resource/schemas'
import type {
  ControlPort,
  CustomerResourceStatusInfo,
  MeasureSchemaType,
  MeteringPort,
  MeteringRelationSchemaType,
  ResourceActivationGroupRelation,
} from '@/features/resource/types'
import type { GroupedPort } from '@/features/resource/utils/portGroups'
import { errorHandler } from '@/utils/errorHandler'
import { convertEnergyPower } from '@/utils/powerEnergyTransformations'

const StyledNoBorderTableRow = styled(TableRow)(() => ({
  '.MuiTableCell-root': {
    border: 'none',
  },
}))

const ResourcePortsTable = ({
  rows,
  columns,
  ariaLabel,
  title,
}: {
  rows: ReactNode
  columns: string[]
  ariaLabel: string
  title: string
}) => {
  return (
    <>
      <CardHeader sx={{ paddingX: 0 }} title={title}></CardHeader>
      <Table aria-label={ariaLabel} size="small" sx={{ tableLayout: 'fixed' }}>
        <TableHead>
          <TableRow>
            {columns.map((column) => (
              <TableCell key={column}>{column}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>{rows}</TableBody>
      </Table>
    </>
  )
}

const ArithmeticOperationCell = ({ meteringPort, resourceId }: { meteringPort: MeteringPort; resourceId: string }) => {
  const { t } = useTranslation()
  const [isEditing, setIsEditing] = useState(false)
  const [currentArithmeticOperation, setCurrentArithmeticOperation] = useState(
    meteringPort.arithmeticOperation || 'PLUS',
  )
  const { pushAlert } = useAlertContext()
  const queryClient = useQueryClient()
  const { updateMeteringPortRelation: updateRelationType, isPending: isArithmeticOperationeUpdatePending } =
    useUpdateMeteringPortRelationMutation()

  useEffect(() => {
    setCurrentArithmeticOperation(meteringPort.arithmeticOperation || 'PLUS')
  }, [meteringPort.arithmeticOperation])

  const form = useForm<MeteringRelationSchemaType>({
    defaultValues: {
      arithmeticOperation: currentArithmeticOperation,
    },
    resolver: zodResolver(getMeteringRelationSchema(t)),
  })

  const onSubmit = async (data: MeteringRelationSchemaType) => {
    try {
      setCurrentArithmeticOperation(data.arithmeticOperation)
      setIsEditing(false)
      await updateRelationType({
        resourceId,
        meteringPortId: meteringPort.powerMeterID,
        arithmeticOperation: data.arithmeticOperation,
      })

      queryClient.invalidateQueries({ queryKey: [GET_RESOURCE_API_ID, { id: resourceId }] })
      pushAlert({
        message: t('resources.metering_port.relation.success_update'),
        severity: 'success',
      })
    } catch (err) {
      const error = errorHandler(err, t('resources.metering_port.relation.error_update'))
      setCurrentArithmeticOperation(meteringPort.arithmeticOperation || 'PLUS')
      pushAlert({
        message: error.message,
        severity: 'error',
      })
    }
  }

  if (isEditing) {
    return (
      <Box width="100%">
        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <Stack direction="column" spacing={1}>
              <SelectFieldController
                fullWidth
                id={`arithmeticOperation-${meteringPort.powerMeterID}`}
                label={t('resources.detail_panel.port_settings_relation')}
                name="arithmeticOperation"
                options={[
                  { value: 'PLUS', label: t('resources.metering_port.relation.additive'), id: 'PLUS' },
                  { value: 'MINUS', label: t('resources.metering_port.relation.subtractive'), id: 'MINUS' },
                ]}
                size="small"
              />
              <SettingEditActions
                isPending={isArithmeticOperationeUpdatePending}
                onCancel={() => setIsEditing(false)}
              />
            </Stack>
          </form>
        </FormProvider>
      </Box>
    )
  }

  // Same reading as the graph node's subtitle: anything that is not MINUS is additive.
  const operation = currentArithmeticOperation === 'MINUS' ? 'MINUS' : 'PLUS'

  return (
    <PortSettingValue
      editLabel={t('resources.metering_port.relation.edit')}
      indicator={<OperatorGlyph operation={operation} />}
      label={
        operation === 'MINUS'
          ? t('resources.metering_port.relation.subtractive')
          : t('resources.metering_port.relation.additive')
      }
      name={t('resources.detail_panel.port_settings_relation')}
      onEdit={() => setIsEditing(true)}
    />
  )
}

/**
 * Save and Cancel for an inline setting edit, stacked one above the other.
 *
 * Side by side they did not fit. The Settings column is 169px, so the edit row has 137px of content
 * box, and a single-word label cannot wrap — in Dutch "Annuleren" painted 13px outside its own
 * button, and in Greek "Αποθήκευση" 32px outside. Only `flex-shrink` kept the row itself inside the
 * cell, and it did so by squeezing the buttons under their own text.
 *
 * Stacked, each button gets the full 137px and the widest label has room to spare. The layout is the
 * same in every locale by construction — deliberately not a wrap fallback that would stack only
 * where a label happens to be long, which is the inconsistency reported on the setting captions
 * earlier: some on one row, some on two, decided by string length.
 *
 * Shared by both edit rows so the two cannot drift; the column stays 169px and CustomButton is
 * untouched, being used across the app.
 */
function SettingEditActions({ isPending, onCancel }: Readonly<{ isPending: boolean; onCancel: () => void }>) {
  const { t } = useTranslation()

  return (
    <Stack direction="column" spacing={1}>
      <CustomButton fullWidth disabled={isPending} size="small" type="submit" variant="contained">
        {t('common.button.save')}
      </CustomButton>
      <CustomButton fullWidth disabled={isPending} size="small" variant="outlined" onClick={onCancel}>
        {t('common.button.cancel')}
      </CustomButton>
    </Stack>
  )
}

const MeasureCell = ({ controlPort, resourceId }: { controlPort: ControlPort; resourceId: string }) => {
  const { t } = useTranslation()
  const [isEditing, setIsEditing] = useState(false)
  // Initialize with UNKNOWN if measure is not defined
  const [currentMeasure, setCurrentMeasure] = useState(controlPort.measure || 'UNKNOWN')
  const { pushAlert } = useAlertContext()
  const queryClient = useQueryClient()
  const { updateControlPortMeasure, isPending: isMeasureUpdatePending } = useUpdateControlPortMeasureMutation()

  // Update current measure when controlPort changes
  useEffect(() => {
    setCurrentMeasure(controlPort.measure || 'UNKNOWN')
  }, [controlPort.measure])

  const form = useForm<MeasureSchemaType>({
    defaultValues: {
      measure: currentMeasure,
    },
    resolver: zodResolver(getMeasureSchema(t)),
  })

  const onSubmit = async (data: MeasureSchemaType) => {
    try {
      setCurrentMeasure(data.measure)
      setIsEditing(false)
      await updateControlPortMeasure({
        resourceId,
        controlPortId: controlPort.controlPortID,
        measure: data.measure,
      })

      queryClient.invalidateQueries({ queryKey: [GET_RESOURCE_API_ID, { id: resourceId }] })
      pushAlert({
        message: t('resources.control_port.measure.success_update'),
        severity: 'success',
      })
    } catch (err) {
      const error = errorHandler(err, t('resources.control_port.measure.error_update'))
      // Reset to previous value on error
      setCurrentMeasure(controlPort.measure || 'UNKNOWN')
      pushAlert({
        message: error.message,
        severity: 'error',
      })
    }
  }

  if (isEditing) {
    return (
      <Box width="100%">
        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <Stack direction="column" spacing={1}>
              <SelectFieldController
                fullWidth
                id={`measure-${controlPort.controlPortID}`}
                label={t('resources.detail_panel.port_settings_measure')}
                name="measure"
                options={[
                  { value: 'TEMPERATURE', label: t('resources.control_port.measure.temperature'), id: 'TEMPERATURE' },
                  { value: 'UNKNOWN', label: t('resources.control_port.measure.unknown'), id: 'UNKNOWN' },
                ]}
                size="small"
              />
              <SettingEditActions isPending={isMeasureUpdatePending} onCancel={() => setIsEditing(false)} />
            </Stack>
          </form>
        </FormProvider>
      </Box>
    )
  }

  // Neutral icons: like the relation's operator, the measure is a category, not a component state,
  // so it takes no palette colour. Unknown is dimmer, being the absence of a value.
  return currentMeasure === 'TEMPERATURE' ? (
    <PortSettingValue
      editLabel={t('resources.control_port.measure.edit')}
      indicator={<ThermostatIcon fontSize="inherit" />}
      label={t('resources.control_port.measure.temperature')}
      name={t('resources.detail_panel.port_settings_measure')}
      onEdit={() => setIsEditing(true)}
    />
  ) : (
    <PortSettingValue
      editLabel={t('resources.control_port.measure.edit')}
      indicator={<HelpOutlineIcon fontSize="inherit" sx={{ color: 'text.disabled' }} />}
      label={t('resources.control_port.measure.unknown')}
      name={t('resources.detail_panel.port_settings_measure')}
      onEdit={() => setIsEditing(true)}
    />
  )
}

/**
 * The editable value for a port row, rendered into the shared Settings column.
 *
 * Only two ports kinds have one — input control ports have a measure, metering ports an arithmetic
 * relation — so output control ports get a dash. That is the cost of giving the controls a fixed
 * x-position instead of trailing whatever prose precedes them, and it was chosen deliberately.
 */
function PortSettingsCell({ port, resourceId }: Readonly<{ port: GroupedPort; resourceId: string }>) {
  // Both editable kinds name their setting the same way, so the column reads as one thing: a bare
  // relation beside a captioned measure looks like a different kind of cell rather than the same
  // setting on a different port. The name is drawn by the cells themselves: inline before the value
  // when reading, as the select's label when editing.
  if (port.kind === 'METERING') {
    return <ArithmeticOperationCell meteringPort={port.meteringPort} resourceId={resourceId} />
  }

  if (port.controlPort.isOutput) {
    return (
      <CustomTypography color="grey.500" variant="inherit">
        -
      </CustomTypography>
    )
  }

  return <MeasureCell controlPort={port.controlPort} resourceId={resourceId} />
}

// Three bars in the rhythm of the table's rows, following the codebase's existing skeleton idiom
// (see GroupsTabContentSkeleton / DataGridSkeleton): MUI Skeletons shaped like the content.
const ACTIVATION_GROUPS_SKELETON_ROWS = ['first', 'second', 'third']

/**
 * Activation-group membership, which — unlike the port sections — always says something, in every
 * state including while it is still loading.
 *
 * Ports are hardware that may simply not exist, so an empty port section can stay hidden. Zero
 * activation groups is a different kind of answer: it is a fact a trader may actively want
 * confirmed, and an absent section cannot express it — leaving the alternative a query against
 * portfolio-manager.
 *
 * That makes distinguishing failure from zero the whole job. The hook returns the same empty array
 * either way, so the states are separated here by `isError` and `isPending`; claiming "not in any
 * activation group" when the fetch failed, or before it has answered, would be worse than the
 * silence it replaces.
 */
function ResourceActivationGroupsSection({
  activationGroups,
  isError,
  isFetching,
  isPending,
  onRetry,
}: Readonly<{
  activationGroups: ResourceActivationGroupRelation[]
  isError: boolean
  isFetching: boolean
  isPending: boolean
  onRetry: () => void
}>) {
  const { t } = useTranslation()

  // The heading is unconditional. The section renders in every state, so there is no empty case to
  // protect against by waiting for the query — and waiting would make the title arrive seconds
  // later, alongside whatever it was introducing.
  const header = (
    <CardHeader sx={{ paddingX: 0 }} title={t('resources.detail_panel.activation_groups_header')}></CardHeader>
  )

  // Still no empty copy before an answer exists: unknown and zero are different things.
  if (isPending) {
    return (
      <>
        {header}
        <Stack aria-busy aria-label={t('resources.detail_panel.activation_groups_loading')} gap={1} role="status">
          {ACTIVATION_GROUPS_SKELETON_ROWS.map((row) => (
            <Skeleton key={row} height={32} variant="rounded" />
          ))}
        </Stack>
      </>
    )
  }

  if (isError) {
    return (
      <>
        {header}
        {/*
         * Additional to useErrorHandler's toast, not a duplicate of it: that toast is transient,
         * generic ("something went wrong") and says nothing about which part of the page is
         * missing. This stays on screen, names the section, and offers the retry.
         */}
        <Alert
          action={
            <CustomButton
              color="inherit"
              // A retry leaves status at 'error', so without this the alert looks inert and invites
              // repeat clicks on a slow connection.
              disabled={isFetching}
              size="small"
              // Wrapped rather than passed directly: refetch reads options off its argument, and
              // handing it a MouseEvent feeds a DOM object into a library API by accident.
              onClick={() => onRetry()}
            >
              {t('resources.detail_panel.activation_groups_retry')}
            </CustomButton>
          }
          aria-label={t('resources.detail_panel.activation_groups_error_label')}
          // Deliberately not the default 'alert'. useErrorHandler already fires an assertive toast
          // for this same failure, and two interrupting announcements for one event is worse than
          // one — the transient toast is the one worth interrupting for; this is here to persist.
          role="status"
          severity="error"
        >
          {t('resources.detail_panel.activation_groups_error')}
        </Alert>
      </>
    )
  }

  if (activationGroups.length === 0) {
    return (
      <>
        {header}
        <CustomTypography color="grey.500" variant="inherit">
          {t('resources.detail_panel.activation_groups_empty')}
        </CustomTypography>
      </>
    )
  }

  // The table renders its own heading, so it stands in for `header` rather than following it.
  return <ResourceActivationGroupsTable activationGroups={activationGroups} />
}

function CustomerResourceStatusInfo({
  customerResourceStatusInfo,
}: {
  readonly customerResourceStatusInfo: CustomerResourceStatusInfo
}) {
  const { t } = useTranslation()

  const columns = [
    {
      label: t('resources.customer_resource_status.status'),
      value: customerResourceStatusInfo?.status ?? '-',
    },
    {
      label: t('resources.customer_resource_status.reason'),
      value: customerResourceStatusInfo?.reason ?? '-',
    },
    {
      label: t('resources.customer_resource_status.description'),
      value: customerResourceStatusInfo?.description ?? '-',
    },
    {
      label: t('resources.customer_resource_status.updated_at'),
      value: customerResourceStatusInfo.updatedAt
        ? DateTime.fromISO(customerResourceStatusInfo.updatedAt).toFormat('dd MMMM, HH:mm')
        : '-',
    },
  ]

  if (
    !customerResourceStatusInfo.status &&
    !customerResourceStatusInfo.reason &&
    !customerResourceStatusInfo.description &&
    !customerResourceStatusInfo.updatedAt
  ) {
    return null
  }

  return (
    <>
      <CardHeader sx={{ paddingX: 0 }} title={t('resources.customer_resource_status.label')}></CardHeader>
      <Box display="flex">
        <Table aria-label={t('resources.customer_resource_status.label')} size="small">
          <TableHead>
            <StyledNoBorderTableRow>
              {columns.map((column) => (
                <TableCell key={column.label}>
                  <CustomTypography color="grey.500" variant="caption">
                    {column.label}
                  </CustomTypography>
                </TableCell>
              ))}
            </StyledNoBorderTableRow>
          </TableHead>
          <TableBody>
            <StyledNoBorderTableRow>
              {columns.map((row) => (
                <TableCell key={row.value}>
                  <CustomTypography variant="inherit">{row.value}</CustomTypography>
                </TableCell>
              ))}
            </StyledNoBorderTableRow>
          </TableBody>
        </Table>
      </Box>
      <Divider />
    </>
  )
}

// The activatable flag only takes effect where the market's control path respects it (see isActivatableSupported).
// Where it does not (e.g. central/cloud mFRR), fall back to the not-applicable label instead of a misleading Yes/No.
function getActivatableLabel(activationGroup: ResourceActivationGroupRelation, t: TFunction<'translation'>): string {
  if (!isActivatableSupported(activationGroup)) {
    return t('common.not_applicable')
  }
  return activationGroup.isActivatable ? t('common.yes') : t('common.no')
}

const ResourceActivationGroupsTable = ({
  activationGroups,
}: {
  activationGroups: ResourceActivationGroupRelation[]
}) => {
  const { t } = useTranslation()

  const columns = useMemo(() => {
    return [
      t('resources.detail_panel.activation_group_uuid'),
      t('resources.detail_panel.activation_group_name'),
      t('resources.detail_panel.qualified_capacity'),
      t('resources.detail_panel.priority'),
      t('resources.detail_panel.activatable'),
    ]
  }, [t])

  return (
    <ResourcePortsTable
      ariaLabel={t('resources.detail_panel.activation_groups_table_label')}
      columns={columns}
      rows={
        <>
          {activationGroups.map((activationGroup) => (
            <TableRow key={activationGroup.activationGroupUuid}>
              <TableCell>
                <Link
                  to={ActivationGroupRouteInformation.ACTIVATION_GROUP_DETAILS.navigationPath(
                    activationGroup.activationGroupUuid,
                  )}
                >
                  {activationGroup.activationGroupUuid}
                </Link>
              </TableCell>
              <TableCell>
                <Link
                  to={ActivationGroupRouteInformation.ACTIVATION_GROUP_DETAILS.navigationPath(
                    activationGroup.activationGroupUuid,
                  )}
                >
                  {activationGroup.activationGroupCode}
                </Link>
              </TableCell>
              <TableCell>{convertEnergyPower(activationGroup.qualifiedCapacity, 'kilowatts')}</TableCell>
              <TableCell>{activationGroup.activationPriority}</TableCell>
              <TableCell>{getActivatableLabel(activationGroup, t)}</TableCell>
            </TableRow>
          ))}
        </>
      }
      title={t('resources.detail_panel.activation_groups_header')}
    />
  )
}

function ResourceGeneralCard() {
  const { resource } = useResourceData()
  const { highlightedPortId, showPort } = usePortHighlight()
  const {
    activationGroups,
    isError: isActivationGroupsError,
    isFetching: isActivationGroupsFetching,
    isPending: isActivationGroupsPending,
    refetch: refetchActivationGroups,
  } = useResourceActivationGroupsQuery(
    { resourceUuid: resource?.resourceID ?? '' },
    { enabled: !!resource?.resourceID },
  )

  if (resource === undefined || resource === null) {
    return null
  }

  const containsPorts = resource.controlPorts.length > 0 || resource.meteringPorts.length > 0

  return (
    <ResourceCard
      content={
        // No gate: the activation-groups section has something to say in every state, so the card
        // always has content, including for a resource with no ports and no status banner.
        <>
          {resource.customerResourceStatusInfo && (
            <CustomerResourceStatusInfo customerResourceStatusInfo={resource.customerResourceStatusInfo} />
          )}
          <ResourceActivationGroupsSection
            activationGroups={activationGroups}
            isError={isActivationGroupsError}
            isFetching={isActivationGroupsFetching}
            isPending={isActivationGroupsPending}
            onRetry={refetchActivationGroups}
          />
          {containsPorts && (
            <ResourcePortsSection
              highlightedPortId={highlightedPortId}
              renderSettings={(port) => <PortSettingsCell port={port} resourceId={resource.resourceID} />}
              resource={resource}
              onShowPort={showPort}
            />
          )}
        </>
      }
    />
  )
}

export default ResourceGeneralCard
